import { useCallback, useEffect, useMemo, useState } from "react";
import api, { getApiErrorMessage } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { formatCurrency, formatProfession } from "../../lib/display";
import { toast } from "sonner";
import {
  ChartNoAxesCombined,
  Clock3,
  Loader2,
  RefreshCw,
  TrendingUp,
  UserRoundCheck,
  UserRoundX,
  UsersRound,
} from "lucide-react";

const periodOptions = [
  { value: "30", label: "Últimos 30 dias" },
  { value: "60", label: "Últimos 60 dias" },
  { value: "90", label: "Últimos 90 dias" },
  { value: "180", label: "Últimos 180 dias" },
];

const inactivityOptions = [
  { value: "30", label: "30 dias sem compra" },
  { value: "60", label: "60 dias sem compra" },
  { value: "90", label: "90 dias sem compra" },
  { value: "180", label: "180 dias sem compra" },
];

const formatLastPurchase = (value) => {
  if (!value) return "Ainda não comprou";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Data não informada" : date.toLocaleDateString("pt-BR");
};

const formatDaysWithoutPurchase = (value) => {
  if (value === null || value === undefined) return "Sem compra registrada";
  return `${value} ${value === 1 ? "dia" : "dias"}`;
};

const StatCard = ({ icon: Icon, label, value, detail, iconClassName }) => (
  <div className="border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-500">{label}</p>
        <p className="mt-2 font-heading text-3xl font-bold text-slate-900">{value}</p>
        {detail && <p className="mt-1 text-sm text-slate-500">{detail}</p>}
      </div>
      <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg ${iconClassName}`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </div>
);

const SectionHeader = ({ title, description, icon: Icon, iconClassName }) => (
  <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4 sm:px-6">
    <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${iconClassName}`}>
      <Icon className="h-4 w-4" />
    </div>
    <div className="min-w-0">
      <h2 className="font-heading text-lg font-semibold text-slate-900">{title}</h2>
      <p className="mt-0.5 text-sm text-slate-500">{description}</p>
    </div>
  </div>
);

const EmptyState = ({ children }) => (
  <div className="px-6 py-10 text-center text-sm text-slate-500">{children}</div>
);

const AdminRelatorios = () => {
  const [report, setReport] = useState(null);
  const [lojas, setLojas] = useState([]);
  const [periodDays, setPeriodDays] = useState("30");
  const [inactivityDays, setInactivityDays] = useState("60");
  const [lojaId, setLojaId] = useState("todas");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let active = true;
    const loadLojas = async () => {
      try {
        const response = await api.get("/lojas");
        if (active) setLojas(response.data);
      } catch (error) {
        if (active) toast.error(getApiErrorMessage(error, "Erro ao carregar as lojas disponíveis."));
      }
    };
    loadLojas();
    return () => {
      active = false;
    };
  }, []);

  const loadReport = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);

    try {
      const response = await api.get("/relatorios/engajamento", {
        params: {
          periodo_dias: Number(periodDays),
          inatividade_dias: Number(inactivityDays),
          limite: 100,
          ...(lojaId !== "todas" ? { loja_id: lojaId } : {}),
        },
      });
      setReport(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Não foi possível carregar os relatórios."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [inactivityDays, lojaId, periodDays]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const lojasById = useMemo(
    () => Object.fromEntries(lojas.map((loja) => [loja.id, loja.nome])),
    [lojas]
  );
  const showStore = lojas.length > 1 && lojaId === "todas";
  const summary = report?.resumo || {};

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-9 w-48 rounded bg-slate-200" />
        <div className="h-20 rounded bg-slate-200" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((index) => <div key={index} className="h-32 rounded bg-slate-200" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-relatorios">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Relatórios</h1>
          <p className="mt-1 text-slate-600">Acompanhe compras, indicações e parceiros que precisam de atenção.</p>
        </div>
        <Button
          variant="outline"
          onClick={() => loadReport({ silent: true })}
          disabled={refreshing}
          className="w-full sm:w-auto"
          title="Atualizar relatórios"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-2 xl:grid-cols-3">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Período das indicações</label>
          <Select value={periodDays} onValueChange={setPeriodDays}>
            <SelectTrigger className="h-10 border-slate-200 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {periodOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Alerta de inatividade</label>
          <Select value={inactivityDays} onValueChange={setInactivityDays}>
            <SelectTrigger className="h-10 border-slate-200 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {inactivityOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {lojas.length > 1 && (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Loja</label>
            <Select value={lojaId} onValueChange={setLojaId}>
              <SelectTrigger className="h-10 border-slate-200 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas as lojas permitidas</SelectItem>
                {lojas.map((loja) => <SelectItem key={loja.id} value={loja.id}>{loja.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard icon={UsersRound} label="Parceiros ativos" value={summary.parceiros_ativos || 0} detail="Base atual do programa" iconClassName="bg-blue-50 text-blue-600" />
        <StatCard icon={UserRoundCheck} label="Indicando no período" value={summary.indicando_no_periodo || 0} detail="Com venda faturada vinculada" iconClassName="bg-emerald-50 text-emerald-600" />
        <StatCard icon={UserRoundX} label="Sem indicar no período" value={summary.sem_indicar_no_periodo || 0} detail={`Nos últimos ${periodDays} dias`} iconClassName="bg-amber-50 text-amber-600" />
        <StatCard icon={Clock3} label="Sem compra" value={summary.sem_compra || 0} detail={`Há ${inactivityDays} dias ou mais`} iconClassName="bg-rose-50 text-rose-600" />
      </div>

      <p className="text-sm text-slate-500">
        Para este relatório, cada venda faturada vinculada ao profissional é considerada uma indicação. Vendas canceladas não entram nos resultados.
      </p>

      <div className="overflow-hidden border border-slate-200 bg-white shadow-sm">
        <SectionHeader
          title="Profissionais que mais indicam"
          description={`Mais vendas faturadas nos últimos ${periodDays} dias.`}
          icon={TrendingUp}
          iconClassName="bg-emerald-50 text-emerald-600"
        />
        {report?.mais_indicam?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-3">Profissional</th>
                  {showStore && <th className="px-5 py-3">Loja</th>}
                  <th className="px-5 py-3 text-center">Vendas</th>
                  <th className="px-5 py-3 text-center">Clientes</th>
                  <th className="px-5 py-3 text-right">Valor faturado</th>
                  <th className="px-5 py-3">Última compra</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {report.mais_indicam.map((parceiro, index) => (
                  <tr key={parceiro.parceiro_id} className="hover:bg-slate-50">
                    <td className="px-5 py-4">
                      <p className="font-medium text-slate-900">{index + 1}. {parceiro.nome}</p>
                      <p className="mt-0.5 text-sm text-slate-500">{formatProfession(parceiro.profissao)}{parceiro.cidade ? ` · ${parceiro.cidade}` : ""}</p>
                    </td>
                    {showStore && <td className="px-5 py-4 text-sm text-slate-600">{lojasById[parceiro.loja_id] || "Não informada"}</td>}
                    <td className="px-5 py-4 text-center font-semibold text-emerald-700">{parceiro.total_vendas}</td>
                    <td className="px-5 py-4 text-center text-slate-700">{parceiro.clientes_distintos}</td>
                    <td className="px-5 py-4 text-right font-medium text-slate-900">{formatCurrency(parceiro.valor_total)}</td>
                    <td className="px-5 py-4 text-sm text-slate-600">{formatLastPurchase(parceiro.ultima_compra)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <EmptyState>Nenhuma venda faturada foi encontrada neste período.</EmptyState>}
      </div>

      <div className="grid grid-cols-1 gap-6 2xl:grid-cols-2">
        <div className="overflow-hidden border border-slate-200 bg-white shadow-sm">
          <SectionHeader
            title="Profissionais sem indicação"
            description={`Sem venda faturada nos últimos ${periodDays} dias.`}
            icon={UserRoundX}
            iconClassName="bg-amber-50 text-amber-600"
          />
          {report?.sem_indicar?.length ? (
            <div className="max-h-[520px] overflow-auto">
              <table className="w-full min-w-[560px]">
                <thead className="sticky top-0 bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Profissional</th>
                    <th className="px-5 py-3">Última compra</th>
                    <th className="px-5 py-3 text-right">Sem comprar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {report.sem_indicar.map((parceiro) => (
                    <tr key={parceiro.parceiro_id} className="hover:bg-slate-50">
                      <td className="px-5 py-4">
                        <p className="font-medium text-slate-900">{parceiro.nome}</p>
                        <p className="mt-0.5 text-sm text-slate-500">{formatProfession(parceiro.profissao)}{parceiro.cidade ? ` · ${parceiro.cidade}` : ""}</p>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">{formatLastPurchase(parceiro.ultima_compra)}</td>
                      <td className="px-5 py-4 text-right text-sm font-medium text-amber-700">{formatDaysWithoutPurchase(parceiro.dias_sem_compra)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyState>Todos os parceiros ativos tiveram indicação neste período.</EmptyState>}
        </div>

        <div className="overflow-hidden border border-slate-200 bg-white shadow-sm">
          <SectionHeader
            title="Parceiros sem compra"
            description={`Sem compra há ${inactivityDays} dias ou mais.`}
            icon={Clock3}
            iconClassName="bg-rose-50 text-rose-600"
          />
          {report?.sem_compra?.length ? (
            <div className="max-h-[520px] overflow-auto">
              <table className="w-full min-w-[560px]">
                <thead className="sticky top-0 bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Profissional</th>
                    <th className="px-5 py-3">Última compra</th>
                    <th className="px-5 py-3 text-right">Sem comprar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {report.sem_compra.map((parceiro) => (
                    <tr key={parceiro.parceiro_id} className="hover:bg-slate-50">
                      <td className="px-5 py-4">
                        <p className="font-medium text-slate-900">{parceiro.nome}</p>
                        <p className="mt-0.5 text-sm text-slate-500">{formatProfession(parceiro.profissao)}{parceiro.cidade ? ` · ${parceiro.cidade}` : ""}</p>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">{formatLastPurchase(parceiro.ultima_compra)}</td>
                      <td className="px-5 py-4 text-right text-sm font-medium text-rose-700">{formatDaysWithoutPurchase(parceiro.dias_sem_compra)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyState>Nenhum parceiro atingiu o limite de inatividade selecionado.</EmptyState>}
        </div>
      </div>

      <div className="flex items-start gap-3 border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800">
        <ChartNoAxesCombined className="mt-0.5 h-5 w-5 flex-shrink-0" />
        <p>Use a lista de parceiros sem compra para ações de reativação e a de profissionais que mais indicam para campanhas de reconhecimento.</p>
      </div>
    </div>
  );
};

export default AdminRelatorios;
